import { FC } from "react";
import {
  Flex,
  AvatarGroup,
  Avatar,
  Text,
  useColorModeValue,
  Tag,
  Box,
} from "@chakra-ui/react";

// Types
import { StringMap } from "types/common";

// Components
import Tooltip from "components/Tooltip";
import Actions from "./Actions";

// Images
import hbarDarkIcon from "assets/hederaDark.svg";
import hbarLightIcon from "assets/hederaLight.svg";

// analytics
import { firebaseEvents } from "utils/firebase";
import { FIREBASE_EVENT_PROPERTIES } from "constants/constants";
import useHashConnect from "hooks/useHashConnect";

interface PropTypes {
  id: string;
  name: string;
  icon?: any;
  platformIcon: any;
  type: string;
  links: StringMap;
  token1: string;
  token1Icon: any;
  token2: string;
  token2Icon: any;
  APY?: string;
  TVL?: string;
  token1IconWidth?: string;
  token1IconHeight?: string;
  tags?: string[];
}

const Item: FC<PropTypes> = ({
  id,
  name,
  platformIcon,
  token1,
  token1Icon,
  token1IconWidth,
  token1IconHeight,
  token2,
  token2Icon,
  type,
  links,
  APY,
  TVL,
  tags,
}: PropTypes) => {
  const { selectedAccount, walletData } = useHashConnect();
  const sendLog = (name: string, data: string) => {
    let firebaseRequest: any = {
      [FIREBASE_EVENT_PROPERTIES.EVENT_NAME]: name,
      [FIREBASE_EVENT_PROPERTIES.DEFI_NAME]: data,
      [FIREBASE_EVENT_PROPERTIES.WALLET_NAME]:
        walletData.pairedWalletData?.name,
      [FIREBASE_EVENT_PROPERTIES.WALLET_ADDRESS]: selectedAccount,
    };
    firebaseEvents(firebaseRequest);
  };

  const hbarIcon = useColorModeValue(hbarLightIcon, hbarDarkIcon);
  return (
    <Flex
      px="6"
      py="4"
      bg="brand.textWhite"
      _dark={{
        bg: "brand.textBg",
      }}
      borderRadius="lg"
      justify="space-between"
      align={{ base: undefined, md: "center" }}
      flexDir={{ base: "column", md: "row" }}
      gap={{ base: "6", md: undefined }}
    >
      <Flex flex={{ md: "2", lg: "5" }}>
        <Flex gap="4" align="center" flex="1">
          {type === "lb" ? (
            <>
              <Avatar name="HABRX" size="sm" src={hbarIcon?.src}></Avatar>
              <Text>HABRX</Text>
            </>
          ) : (
            <>
              <Flex flexDir="row">
                <AvatarGroup spacing="-4" size="sm">
                  <Avatar
                    name={token1}
                    src={token1Icon?.src}
                    zIndex="1"
                    borderWidth="0"
                    h={token1IconHeight || "34px"}
                    w={token1IconWidth || "34px"}
                    bgColor="transparent"
                  ></Avatar>
                  <Avatar
                    name={token2}
                    src={token2Icon?.src}
                    borderWidth="0"
                    h="34px"
                    w="34px"
                    bgColor="transparent"
                  ></Avatar>
                </AvatarGroup>
                <Flex flexDir={"column"} justifyContent="center" ml="12px">
                  <Text fontWeight="600" fontSize="16px">
                    {token1} / {token2}
                  </Text>
                  <Box>
                    {tags?.map((tag) => (
                      <Tag
                        key={tag}
                        size="sm"
                        bg="brand.defiPreview.bg"
                        color="brand.defiPreview.text"
                        mt="1"
                        fontWeight="700"
                        fontSize="10px"
                        textAlign="center"
                      >
                        {tag}
                      </Tag>
                    ))}
                  </Box>
                </Flex>
              </Flex>
            </>
          )}
        </Flex>
        <Flex align="center" display={{ base: "inherit", lg: "none" }}>
          <Tooltip label={name} bg="brand.toolTipBg" color="brand.toolTipText">
            <Avatar
              h="34px"
              w="34px"
              name="platform"
              src={platformIcon?.src}
              bg="none"
            />
          </Tooltip>
        </Flex>
      </Flex>

      <Flex flex={{ md: "1", lg: "3" }} gap="16" align="center">
        <Text width="130px">
          APR
          <Text as="span" ml=".5rem" fontWeight="600">
            {APY ? `${APY} %` : "-"}
          </Text>
        </Text>
      </Flex>
      <Flex flex={{ md: "2", lg: "4" }} gap="16" align="center">
        <Text>
          TVL
          <Text as="span" ml=".5rem" fontWeight="600">
            {TVL || "-"}
          </Text>
        </Text>
      </Flex>
      <Flex
        flex={{ md: "1", lg: "2" }}
        justify="space-around"
        display={{ base: "none", lg: "inherit" }}
      >
        <Tooltip label={name} bg="brand.toolTipBg" color="brand.toolTipText">
          <Avatar
            h="36px"
            w="36px"
            name="platform"
            src={platformIcon?.src}
            bg="none"
          />
        </Tooltip>
      </Flex>

      <Actions
        id={id}
        type={type}
        links={links}
        trackEventDefi={(data: string) => {
          sendLog("Defi", data);
        }}
      />
    </Flex>
  );
};

export default Item;
